/** @jsxImportSource preact */
import { h, ComponentChildren } from 'preact';
import { useEffect, useId, useRef, useState } from 'preact/hooks';

const GAP = 6;
const MAX_WIDTH = 360;
const CLOSE_DELAY_MS = 120;

/**
 * Hover / focus / tap tooltip for a small inline element (the severity
 * pills). The box is `position: fixed` from the trigger's viewport rect, so
 * the modal's scrolling panel cannot clip it; it opens on whichever side of
 * the trigger has more room and scrolls inside itself if even that is not
 * enough. It closes on any scroll outside itself, because a fixed box would
 * otherwise stay put while its trigger moves.
 *
 * The box is a DOM child of the trigger, so moving the pointer onto it
 * re-enters the trigger and cancels the pending close: a long box can be
 * scrolled with the mouse.
 */
export function HoverTip({ content, children }: { content: ComponentChildren; children: ComponentChildren }) {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);
  const closeTimer = useRef<number | undefined>(undefined);
  const [pos, setPos] = useState<h.JSX.CSSProperties | null>(null);
  const id = useId();

  const open = () => {
    window.clearTimeout(closeTimer.current);
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(MAX_WIDTH, vw - 16);
    const left = Math.min(Math.max(8, r.left + r.width / 2 - width / 2), vw - width - 8);
    setPos(r.top > vh - r.bottom
      ? { left, width, bottom: vh - r.top + GAP, maxHeight: r.top - GAP - 8 }
      : { left, width, top: r.bottom + GAP, maxHeight: vh - r.bottom - GAP - 8 });
  };

  const close = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setPos(null), CLOSE_DELAY_MS);
  };

  useEffect(() => {
    if (!pos) return;
    const onScroll = (e: Event) => {
      if (tipRef.current && e.target instanceof Node && tipRef.current.contains(e.target)) return;
      setPos(null);
    };
    const onResize = () => setPos(null);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [pos]);

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  return (
    <span
      ref={triggerRef}
      tabIndex={0}
      aria-describedby={pos ? id : undefined}
      onMouseEnter={open}
      onMouseLeave={close}
      onFocus={open}
      onBlur={close}
      onClick={open}
      style={{ display: 'inline-block', cursor: 'help', borderRadius: 999 }}
    >
      {children}
      {pos && (
        <span
          ref={tipRef}
          id={id}
          role="tooltip"
          style={{
            position: 'fixed',
            ...pos,
            zIndex: 2147483647,
            boxSizing: 'border-box',
            overflowY: 'auto',
            display: 'block',
            padding: '8px 10px',
            borderRadius: 6,
            background: '#1f2937',
            color: '#ffffff',
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 1.45,
            textAlign: 'left',
            whiteSpace: 'normal',
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
            cursor: 'auto',
          }}
        >
          {content}
        </span>
      )}
    </span>
  );
}
